// External Imports
import { useState, useContext } from "react";
import Modal from "../UI/Modal";
import Heading from "../UI/Heading";
import { MdClose } from "react-icons/md";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";
import { requestAdminAPI } from "../../api/posh-api";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import { UtilityContext } from "../../context/util-context";

// Internal Imports

/** ========================================================================
 * * ChargeInvoiceModal Component
 * This component renders a form to charge an invoice.
 * ========================================================================== */

export default function ChargeInvoiceModal({
  open,
  setOpen,
  invoice,
  onComplete,
}) {
  const [loading, setLoading] = useState(false);
  const { setWarning } = useContext(UtilityContext);

  /* ====================== CHARGE INVOICE  ====================== */
  const submitHandler = async () => {
    // Validate payment method availability
    const hasPaymentMethods = invoice?.customer?.paymentMethods?.length > 0;
    
    if (!hasPaymentMethods) {
      setWarning("Please select or connect a payment method before charging the invoice.");
      return;
    }

    // Get payment method value from DOM (SelectPair is uncontrolled)
    const paymentMethodElement = document.getElementById("paymentMethodId");
    
    if (!paymentMethodElement) {
      setWarning("Please select or connect a payment method before charging the invoice.");
      return;
    }

    const paymentMethodId = paymentMethodElement.value;

    if (!paymentMethodId || paymentMethodId === "No Payment Methods Found") {
      setWarning("Please select or connect a payment method before charging the invoice.");
      return;
    }

    // Get discount code value
    const codeElement = document.getElementById("code");
    const code = codeElement?.value || "";

    try {
      setLoading(true);
      await requestAdminAPI("charge-invoice", {
        invoiceId: invoice.id,
        paymentMethodId: paymentMethodId,
        code: code,
      });
      onComplete && onComplete();
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  if (!invoice) return null;
  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal">
        <Heading title="Charge Invoice" id={invoice?.id}>
          <MdClose onClick={() => setOpen(false)} className="icon" />
        </Heading>
        <div className="info lg:grid-cols-3">
          <DetailPair
            label="Customer"
            value={`${invoice.customer?.firstName} ${invoice.customer?.lastName}`}
          />
          <DetailPair
            label="Booking"
            value={
              invoice.booking
                ? `${invoice.booking?.vehicle.year} ${invoice.booking?.vehicle?.make?.name} ${invoice.booking?.vehicle?.model}`
                : "-"
            }
          />
          <DetailPair label="Category" value={invoice.category} />
          <div className="lg:col-span-3">
            <DetailPair
              label="Description"
              classes="font-semibold text-stone-200 text-base"
              value={invoice.description}
            />
          </div>
          <DetailPair label="Subtotal" value={`$${invoice.subtotal}`} />
          <DetailPair label="Taxes" value={`$${invoice.taxes}`} />
          <DetailPair
            label="Total"
            classes="font-semibold text-xl"
            value={`$${invoice.amount}`}
          />
          <SelectPair
            label="Payment Method"
            options={invoice.customer?.paymentMethods?.length > 0
              ? invoice.customer.paymentMethods
                  .filter((m) => !m.deleted)
                  .map((m) => ({
                    id: m.id,
                    name: `${m.brand?.toUpperCase()} ${m.last4}`,
                  }))
              : []
            }
            edit={invoice.customer?.paymentMethods?.length > 0}
            inputOptions={{
              id: "paymentMethodId",
              defaultValue:
                invoice.customer?.defaultPaymentMethodId ||
                (invoice.customer?.paymentMethods?.length > 0
                  ? invoice.customer.paymentMethods.find((m) => !m.deleted)?.id
                  : "No Payment Methods Found"),
              disabled: invoice.customer?.paymentMethods?.length === 0,
            }}
          />
          {invoice.customer?.paymentMethods?.length === 0 && (
            <div className="lg:col-span-3">
              <p className="text-yellow-600 text-sm mt-1">
                No payment methods available. Please connect a payment method for this customer.
              </p>
            </div>
          )}
          <DetailPair
            label="Discount Code"
            edit={true}
            inputOptions={{
              type: "text",
              id: "code",
              placeholder: "optional",
            }}
          />
        </div>

        {loading ? (
          <LoadingSpinnerWrapper />
        ) : (
          <div className="grid-cols-2 info">
            <div onClick={() => setOpen(false)} className="btn-secondary">
              Cancel
            </div>
            <button
              onClick={submitHandler}
              disabled={!invoice?.customer?.paymentMethods?.length}
              className={`btn-primary ${
                !invoice?.customer?.paymentMethods?.length
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              Charge Customer
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
